import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, TemplateRef } from '@angular/core';
import { DialogRef, ModalService } from '../../services/modal.service';

@Component({
  standalone: false,
  selector: 'app-modals-widget',
  templateUrl: 'modals-widget.component.html',
  styleUrls: ['modals-widget.component.scss'],
  animations: [
    trigger('openCloseBackdrop', [
      state('open', style({ opacity: 0.8 })),
      state('closed', style({ opacity: 0, display: 'none' })),
      transition('open => closed', [animate('0.2s')]),
      transition('closed => open', [style({ display: 'block' }), animate('0.2s')]),
    ]),
    trigger('openCloseModal', [
      state('open', style({ top: 0 })),
      state('closed', style({ top: '-100%' })),
      transition('open => closed', [animate('0.2s')]),
      transition('closed => open', [style({ top: '-100%' }), animate('0.2s')]),
    ]),
  ],
})
export class ModalsWidgetComponent {
  dialogs: ViewDialog[] = [];

  backdropShow = false;

  private nextDialogId = 0;

  constructor(public service: ModalService) {
    this.service.modals.subscribe(dialogConfig => this.handleNewDialog(dialogConfig));
  }

  private handleNewDialog(dialogConfig: DialogRef): void {
    const isFirstDialog = this.backdropShow === false;
    const dialog = this.createDialog(dialogConfig);

    this.markPreviousDialogsNotTop();
    this.dialogs.push(dialog);

    setTimeout(() => (dialog.isOpen = true), 0);

    this.subscribeToDialogDestruction(dialogConfig, dialog);

    if (isFirstDialog) {
      this.onBackdropShow();
    }
  }

  private createDialog(dialogConfig: DialogRef): ViewDialog {
    const context = dialogConfig.getContext();
    const id = this.nextDialogId++;

    const dialog: ViewDialog = {
      origin: dialogConfig,
      id,
      className: this.getSizeClass(dialogConfig),
      title: context.title,
      body: context.body,
      component: context.component,
      isOpen: false,
      isConfirm: context.type === 'confirm',
      isTop: true,
    };

    if (dialog.isConfirm) {
      dialog.className += ' modal-dialog-confirm';
    }

    return dialog;
  }

  private markPreviousDialogsNotTop(): void {
    this.dialogs.forEach(d => (d.isTop = false));
  }

  private subscribeToDialogDestruction(dialogConfig: DialogRef, dialog: ViewDialog): void {
    const id = dialog.id;

    dialogConfig.onDestroy.subscribe(() => {
      dialog.isOpen = false;

      if (this.dialogs.length === 1) {
        this.onBackdropHide();
      }

      setTimeout(() => {
        this.dialogs = this.dialogs.filter(d => d.id !== id);
        if (this.dialogs.length) {
          this.dialogs[this.dialogs.length - 1].isTop = true;
        }
      }, 100);
    });
  }

  onClickBackdrop(dialog: ViewDialog) {
    if (!dialog.origin.getContext().isBlocking) {
      dialog.origin.close(false);
    }
  }

  onClickContent(ev) {
    ev.stopPropagation();
  }

  onBackdropShow() {
    document.body.classList.add('modal-open');
    this.backdropShow = true;
  }

  onBackdropHide() {
    document.body.classList.remove('modal-open');
    this.backdropShow = false;
  }

  private getSizeClass(dialog: DialogRef): string {
    const context = dialog.getContext();
    if (context.size) {
      return 'modal-dialog ' + context.size;
    }
    if (context.dialogClass) {
      return context.dialogClass;
    }
    return 'modal-dialog';
  }
}

interface ViewDialog {
  id: number;
  origin: DialogRef;
  title: string;
  body: string;
  component?: TemplateRef<HTMLElement>;
  className: string;
  isOpen: boolean;
  isConfirm: boolean;
  isTop: boolean;
}
